import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarX, CheckCircle2, Bell, ShieldQuestion, ShoppingBasket } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { Cargando } from '../ui/EstadoDePantalla';

// La campana. Dos cosas distintas conviven aquí, y en este orden:
//   1. Tus avisos: lo que te toca a ti saber. El más importante es que un
//      movimiento tuyo quedó esperando autorización — típicamente uno que
//      registraste sin conexión y que al sincronizar resultó superar el
//      umbral. Sin este aviso te quedas creyendo que despachaste mercancía
//      que en realidad sigue en el estante.
//   2. Lo que hay que reponer: el stock por debajo de su mínimo.
const ICONO_TIPO = {
  SOLICITUD_PENDIENTE: ShieldQuestion,
  ANOMALIA: AlertTriangle,
  DISCREPANCIA_CONTEO: AlertTriangle,
  STOCK_BAJO: AlertTriangle,
  // Lo levanta el temporizador nocturno (N-12). Desde N-13 un lote vencido
  // no solo se pierde: el mostrador deja de venderlo, así que frena ventas
  // hasta que alguien lo retira.
  CADUCIDAD: CalendarX,
};

const pedir = () =>
  Promise.all([
    api.get('/notificaciones').catch(() => []),
    api.get('/productos?bajo_minimo=true&solo_activos=true&limite=100').catch(() => []),
  ]);

export default function Alertas({ irA }) {
  const [avisos, setAvisos] = useState(null);
  const [bajos, setBajos] = useState(null);
  const { esEmpresa } = useAuth();

  const cargar = useCallback(
    () => pedir().then(([n, p]) => { setAvisos(n); setBajos(p); }),
    [],
  );

  useEffect(() => {
    let vigente = true;
    pedir().then(([n, p]) => {
      if (!vigente) return;
      setAvisos(n);
      setBajos(p);
    });
    return () => { vigente = false; };
  }, []);

  async function marcarLeida(id) {
    await api.post(`/notificaciones/${id}/leer`, {});
    cargar();
  }

  const cargando = avisos === null || bajos === null;
  const nada = !cargando && avisos.length === 0 && bajos.length === 0;

  return (
    <div className="space-y-4">
      {/* Título=qué, subtítulo=para qué (Doctrina 2): la pantalla se explica
          sola sin que nadie tenga que abrirla para saber qué encontrará. */}
      <div className="pt-2">
        <h2 className="text-lg font-semibold text-gray-900">Alertas</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Lo que necesita tu atención: avisos para ti y productos por debajo de su mínimo.
        </p>
      </div>

      {cargando && <Cargando filas={4} />}

      {nada && (
        <div className="flex flex-col items-center text-center text-gray-500 py-10 gap-2">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
          <p className="text-sm">Nada pendiente. Todo por encima del mínimo y sin avisos para ti.</p>
        </div>
      )}

      {avisos?.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase">
            <Bell className="h-3.5 w-3.5" /> Tus avisos
          </div>
          {avisos.map((n) => {
            const Icono = ICONO_TIPO[n.tipo] || Bell;
            const propio = n.tipo === 'SOLICITUD_PENDIENTE';
            return (
              <div
                key={n.id}
                className={`bg-white border rounded-xl p-3 flex items-start gap-3 ${
                  propio ? 'border-blue-200' : 'border-gray-200'
                }`}
              >
                <div className={`rounded-lg p-2 shrink-0 ${propio ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                  <Icono className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{n.titulo}</div>
                  {n.mensaje && <div className="text-sm text-gray-600 mt-0.5">{n.mensaje}</div>}
                </div>
                <button
                  onClick={() => marcarLeida(n.id)}
                  className="text-xs font-semibold text-[var(--color-marca)] shrink-0"
                >
                  Visto
                </button>
              </div>
            );
          })}
        </section>
      )}

      {bajos?.length > 0 && (
        <section className="space-y-2">
          <div className="text-xs font-bold text-gray-500 uppercase">Hay que reponer</div>
          {/* EN CUENTA PERSONAL ESTO YA ESTÁ APUNTADO, y hasta ahora no
              había cómo llegar a donde se apuntó. Ver la lista de lo que
              falta sin poder abrir la lista de compras es exactamente el
              hueco que el Bloque J vino a cerrar. En empresa no se ofrece:
              allí lo que falta va al flujo de compras, con proveedor y
              orden, que es otro camino. */}
          {!esEmpresa && irA && (
            <button
              onClick={() => irA('lista-compras', {})}
              className="w-full flex items-center gap-2 text-sm font-semibold
                         text-[var(--color-marca)] bg-white border border-gray-200
                         rounded-xl px-3 py-2.5"
            >
              <ShoppingBasket className="h-4 w-4" aria-hidden="true" />
              Ver mi lista de compras
            </button>
          )}
          {bajos.map((p) => {
            const existencia = Number(p.existencia_total ?? 0);
            const minimo = Number(p.stock_minimo ?? 0);
            const faltante = Math.max(0, minimo - existencia);
            return (
              <div key={p.sku} className="bg-white border border-amber-200 rounded-xl p-3 flex items-center gap-3">
                <div className="bg-amber-100 text-amber-700 rounded-lg p-2">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{p.nombre}</div>
                  <div className="text-xs font-mono text-gray-500">{p.sku}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold tabular-nums text-amber-700">{existencia}</div>
                  <div className="text-[10px] text-gray-500">mín. {minimo} · faltan {faltante}</div>
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
